import { TCarousel } from 'widgets/global/Carousel';
import { TRefElementInstance } from 'widgets/toolbox/RefElement';

/**
 * Tile image carousel with infinite loop.
 *
 * Clones the first and last slides at opposite ends of the track so that
 * next/prev always produces a natural slide animation. After the smooth
 * scroll lands on a clone the position is instantly reset to the real slide.
 *
 * @param Carousel Base carousel widget
 * @returns TileImageCarousel class
 */
function TileImageCarouselClassCreator(Carousel: TCarousel) {
    class TileImageCarousel extends Carousel {
        private isDragging = false;

        private realSlideCount = 0;

        private slideWidth = 0;

        private isLoopJumping = false;

        private loopTargetPage: number | null = null;

        prefs() {
            return {
                ...super.prefs(),
                enableAutoVideoDisplay: false
            };
        }

        initCarousel() {
            this.setupClones();
            super.initCarousel();
        }

        /**
         * Clone first→end and last→start, then instantly scroll to the
         * real first slide (index 1 after the prepended clone).
         */
        private setupClones() {
            const track = this.ref(this.prefs().elemCarouselTrack).get();

            if (!track) {
                return;
            }

            const items = Array.from(track.children) as HTMLElement[];

            if (items.length < 2) {
                return;
            }

            this.realSlideCount = items.length;

            const firstClone = items[0].cloneNode(true) as HTMLElement;
            const lastClone = items[items.length - 1].cloneNode(true) as HTMLElement;

            firstClone.setAttribute('aria-hidden', 'true');
            lastClone.setAttribute('aria-hidden', 'true');
            firstClone.classList.add('m-clone');
            lastClone.classList.add('m-clone');

            track.appendChild(firstClone);
            track.insertBefore(lastClone, track.firstChild);

            this.slideWidth = items[0].offsetWidth;

            track.style.scrollBehavior = 'auto';
            track.scrollLeft = this.slideWidth;
            track.style.scrollBehavior = '';
        }

        scrollToNextPage() {
            if (this.realSlideCount > 1 && this.getCurrentPageIndex() === this.realSlideCount - 1) {
                this.loopTargetPage = 0;
                this.syncPaginationTo(0);
            }

            super.scrollToNextPage();
        }

        scrollToPrevPage() {
            if (this.realSlideCount > 1 && this.getCurrentPageIndex() === 0) {
                this.loopTargetPage = this.realSlideCount - 1;
                this.syncPaginationTo(this.realSlideCount - 1);
            }

            super.scrollToPrevPage();
        }

        onScroll() {
            if (this.isLoopJumping) {
                return;
            }

            super.onScroll();
        }

        onScrollEnd() {
            if (this.realSlideCount > 1) {
                this.correctLoopPosition();
            }

            super.onScrollEnd();
        }

        /**
         * After a smooth scroll finishes on a clone, silently jump to the
         * matching real slide so the loop is seamless.
         */
        private correctLoopPosition() {
            const track = this.ref(this.prefs().elemCarouselTrack).get();

            if (!track || this.isLoopJumping) {
                return;
            }

            const firstRealLeft = this.slideWidth; // clone occupies index 0
            const lastRealLeft = this.realSlideCount * this.slideWidth;

            // Landed on the prepended clone (before first real slide)
            if (track.scrollLeft < firstRealLeft * 0.5) {
                this.jumpTo(track, lastRealLeft);
                return;
            }

            // Landed on the appended clone (after last real slide)
            const appendedCloneLeft = (this.realSlideCount + 1) * this.slideWidth;

            if (track.scrollLeft > appendedCloneLeft - this.slideWidth * 0.5) {
                this.jumpTo(track, firstRealLeft);
            }
        }

        // isLoopJumping stays true until the next animation frame, suppressing
        // scroll events fired by the instant reposition. A user-triggered
        // navigation during that single-frame window would be swallowed.
        private jumpTo(track: HTMLElement, left: number) {
            this.isLoopJumping = true;

            track.style.scrollBehavior = 'auto';
            track.scrollLeft = left;
            track.style.scrollBehavior = '';

            requestAnimationFrame(() => {
                this.isLoopJumping = false;
                this.loopTargetPage = null;
                this.updateCarouselMetric();
                this.updateCarouselState();
                this.syncPagination();
            });
        }

        getCurrentPageIndex(): number {
            if (this.realSlideCount < 2) {
                return super.getCurrentPageIndex();
            }

            const track = this.ref(this.prefs().elemCarouselTrack).get();

            if (!track) {
                return 0;
            }

            const rawIndex = Math.round(track.scrollLeft / this.slideWidth);

            return Math.max(0, Math.min(rawIndex - 1, this.realSlideCount - 1));
        }

        createPaginationElements() {
            if (this.realSlideCount < 2) {
                super.createPaginationElements();
                return;
            }

            const paginationData = Array.from({ length: this.realSlideCount }, (_, i) => ({ page: i }));

            this.pagination = new Promise((resolve) => {
                this.render(undefined, { pagination: paginationData }, this.ref('pagination')).then(() => {
                    return resolve(this.ref('pagination').get() as HTMLElement);
                });
            });
        }

        private syncPagination() {
            this.syncPaginationTo(this.getCurrentPageIndex());
        }

        /**
         * Set the active pagination segment to a specific index, clearing all others.
         */
        private syncPaginationTo(pageIndex: number) {
            if (!this.pagination) {
                return;
            }

            const cls = this.prefs().pageCurrentClass;

            this.pagination.then((el: HTMLElement) => {
                if (!el) {
                    return;
                }

                const children = el.children;

                for (let i = 0; i < children.length; i++) {
                    children[i].classList.toggle(cls, i === pageIndex);
                }
            });

            this.currentPage = pageIndex;
        }

        setActivePagination() {
            if (this.realSlideCount < 2) {
                super.setActivePagination();
                return;
            }

            if (this.loopTargetPage !== null) {
                this.syncPaginationTo(this.loopTargetPage);
                return;
            }

            this.syncPagination();
        }

        updateCarouselState() {
            if (this.realSlideCount < 2) {
                super.updateCarouselState();
                return;
            }

            const carousel = this.ref('self');

            carousel.toggleClass('m-no_scroll', this.hasNoScroll());
            carousel.addClass('m-prev_visible');
            carousel.addClass('m-next_visible');
            this.ref(this.prefs().elemPrevButton).attr('disabled', false);
            this.ref(this.prefs().elemNextButton).attr('disabled', false);
            this.setPageIndex();
        }

        onMouseDown(el: TRefElementInstance, event: MouseEvent) {
            this.isDragging = false;
            super.onMouseDown(el, event);
        }

        onMouseMove(element: HTMLElement, event: TouchEvent | MouseEvent) {
            this.isDragging = true;
            super.onMouseMove(element, event);
        }

        onMouseUp() {
            super.onMouseUp();

            setTimeout(() => {
                this.isDragging = false;
            }, 0);
        }

        onTileLinkClick(el: TRefElementInstance, event: MouseEvent) {
            if (this.isDragging) {
                event.preventDefault();
                event.stopPropagation();
            }
        }
    }

    return TileImageCarousel;
}

export type TTileImageCarousel = ReturnType<typeof TileImageCarouselClassCreator>;

export type TTileImageCarouselInstance = InstanceType<TTileImageCarousel>;

export default TileImageCarouselClassCreator;
